import React, { Component } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import i18n from 'i18n-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { withRouter, Redirect } from 'react-router-dom';

import Image from 'components/Image';
import Progress from 'components/Progress';
import APIHandler from 'config/APIHandler';
import DataHandler from 'config/DataHandler';
import StorageHandler from 'config/StorageHandler';
import { useImgix } from 'hooks/useImgix';

import { storageKeys } from 'config/settings.json';

const trackRetainChapterClick = (chapterId, chapterName) => {
  DataHandler.matchAppId().then((appId) => {
    StorageHandler.load(storageKeys.user).then((userObj) => {
      const email = userObj?.email ?? 'Guest';
      void APIHandler.trackEvent(appId, email, 'retain', 'chapter', String(chapterId), chapterName);
    });
  });
};

const Overlay = (props) => {
  const { id } = props;
  return (
    <div id={`overlay_${id}`} className="overlay">
      <i id={`lock_${id}`} className="fas fa-lock" />
    </div>
  );
};

const TrainingCardLogoImage = ({ src }) => {
  const { src: imgixSrc } = useImgix(src);
  if (!imgixSrc) return null;
  return <img className="trainingCard__logoImage" src={imgixSrc} alt="" />;
};

class TrainingCard extends Component {
  constructor(props) {
    super(props);

    this.state = {
      redirect: '',
      loading: true,
      lang: '',
    };
  }

  UNSAFE_componentWillMount() {
    StorageHandler.load(storageKeys.lang).then(({ langFile: lang }) => {
      this.setState({ lang });
    });
  }

  redirect(url, page, path) {
    const { redirPath, history } = this.props;
    if (page === undefined) {
      history.push(`${path !== undefined ? path : redirPath}${url}/0`);
    } else {
      history.push(`${path !== undefined ? path : redirPath}${url}/${page}`);
    }
  }

  render() {
    const {
      appId,
      chapter,
      chapterCount,
      chapterCountMin,
      history,
      index,
      locked,
      foundation,
      lockedSubtitle,
      redirPath,
      comingSoon,
      reviewCompletedInRetain,
    } = this.props;

    const { chaptersProgress } = this.props;
    const { loading, redirect, lang } = this.state;
    const { chapterImage, chapterID, chapterTitle, badge, translations } = chapter;

    const totalPages = chaptersProgress?.total_pages ?? chapterCount ?? 1;
    const lastPage = chaptersProgress?.last_page ?? chaptersProgress?.progress ?? 0;
    const rawPercentage = this.props.progress ?? (totalPages > 0 ? Math.floor((lastPage / totalPages) * 100) : 0);

    const isComplete = totalPages > 0 && (lastPage >= totalPages - 1 || rawPercentage >= 100);
    const progress = isComplete ? 100 : Math.min(100, Math.max(0, rawPercentage));

    let translated;

    if (lang !== 'en') {
      const translation = translations?.filter(({ locale }) => locale.includes(lang.toUpperCase()));
      translated = translation[0];
    }

    let buttonText;
    let redir;
    switch (progress) {
      case 100:
        if (reviewCompletedInRetain) {
          buttonText = i18n.translate('trainingScreens.review');
          redir = () => {
            trackRetainChapterClick(chapterID, chapterTitle);
            history.push({
              pathname: `/main/ret/chapter/${chapterID}/0`,
              search: '?customRedir=training',
            });
          };
        } else {
          buttonText = i18n.translate('trainingScreens.restart');
          redir = () => {
            history.push(`/main/training/chapter/${chapterID}/0`);
          };
        }
        break;
      case undefined:
      case 0:
        buttonText = i18n.translate('trainingScreens.start');
        break;
      default:
        buttonText = i18n.translate('trainingScreens.continue');
    }

    if (redirPath.includes('/main/ret/chapter/')) {
      redir = () => {
        trackRetainChapterClick(chapterID, chapterTitle);
        history.push(`/main/ret/chapter/${chapterID}/0`);
      };
      buttonText = i18n.translate('trainingScreens.review');
    }

    if (redir === undefined) {
      redir = (path) => {
        this.redirect(chapterID, lastPage, path);
      };
    }

    const title = chapterTitle.split(':');

    return (
      <div
        className={clsx('trainingCard', { loading, locked, comingSoon, foundation })}
      >
        {(translated?.badge ?? badge) && progress >= 100 && (
          <a
            href={APIHandler.getDownloadURL(translated?.badge ?? badge, translated?.title ?? chapterTitle)}
            className="badge"
          >
            <FontAwesomeIcon icon={['far', 'file-certificate']} />
            {i18n.translate('general.downloadBadge')} <br />
          </a>
        )}

        <div
          role="button"
          tabIndex={index}
          onKeyPress={() => {
            redir();
          }}
          onClick={() => {
            redir();
          }}
        >
          <div className="textContainer">
            <div className="trainingCard__logo-slot">
              {chapter.logo_image && <TrainingCardLogoImage src={chapter.logo_image} />}
            </div>
            {!loading && (
              <div>
                {comingSoon && (
                  <React.Fragment>
                    <h3 className="global__text--title">{translated?.title ?? chapterTitle}</h3>
                    <h4>{i18n.translate('trainingScreens.comingSoon')}</h4>
                  </React.Fragment>
                )}
                {translated && <h3 className="global__text--title">{translated.title}</h3>}
                {!comingSoon && !translated && (
                  <React.Fragment>
                    <h3
                      className="global__text--title"
                      dangerouslySetInnerHTML={{
                        __html: title[0].replace('&', '</br>&'),
                      }}
                    />
                    <span>{title[1]}</span>
                  </React.Fragment>
                )}
                {!locked && (
                  <p className="duration">
                    <FontAwesomeIcon icon={['far', 'clock']} />
                    {` ${chapterCountMin} ${i18n.translate('trainingScreens.durationInMinutes')}`}
                  </p>
                )}
                {!locked && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      redir(redirPath);
                    }}
                  >
                    {buttonText}
                  </button>
                )}
                {lockedSubtitle && foundation && locked && !comingSoon && (
                  <p>
                    {i18n.translate('trainingScreens.unlockFoundation', {
                      number: index + 1,
                    })}
                  </p>
                )}
                {!lockedSubtitle && foundation && locked && !comingSoon && (
                  <p>
                    {i18n.translate('trainingScreens.unlockFoundation', {
                      number: index,
                    })}
                  </p>
                )}
                {lockedSubtitle && !foundation && locked && !comingSoon && <p>{lockedSubtitle}</p>}
                {!lockedSubtitle && !foundation && locked && !comingSoon && (
                  <React.Fragment>
                    <p>{i18n.translate('trainingScreens.unlockSpecialization')}</p>
                    {/* Note: Icon is positioned absolutely to avoid it affecting layout for title and subtitle compared to labels without icon. */}
                    <FontAwesomeIcon icon={['fas', 'lock']} className="trainingCard__lock-icon" />
                  </React.Fragment>
                )}
              </div>
            )}
          </div>
          <div className="imageContainer">
            <Image
              appId={appId}
              load={() => {
                this.setState({ loading: false });
              }}
              src={`https://front.travpromobile.com/${import.meta.env.VITE_APP_ID}/${chapterImage}`}
              alt={chapterTitle}
              gradient
              staticImage
            />
            {!loading && locked && <Overlay id={index} />}
            {!loading && <span className="chapterNumber">{`0${index + 1}`.slice(-2)}</span> && (
              <span className="chapterComplete">
                {progress >= 100 && !locked && (
                  <FontAwesomeIcon icon={['far', 'check-circle']} className="chapterComplete" />
                )}
              </span>
            )}
          </div>
          {!locked && !loading && <Progress id={index} value={progress} />}
          {!locked && redirect.length > 0 && <Redirect to={redirect} />}
        </div>
      </div>
    );
  }
}

export default withRouter(TrainingCard);

TrainingCard.propTypes = {
  appId: PropTypes.number.isRequired,
  chapter: PropTypes.shape({
    chapterID: PropTypes.string,
    chapterImage: PropTypes.string,
    chapterImagePro: PropTypes.string,
    chapterItemCount: PropTypes.number,
    chapterNumber: PropTypes.string,
    chapterTitle: PropTypes.string,
    logo_image: PropTypes.string,
  }).isRequired,
  chapterCountMin: PropTypes.number,
  chapterCount: PropTypes.number,
  chaptersProgress: PropTypes.shape({
    chapter: PropTypes.string,
    last_page: PropTypes.number,
    percentage: PropTypes.number,
    progress: PropTypes.number,
    total_pages: PropTypes.number,
  }),
  progress: PropTypes.number,
  history: PropTypes.shape().isRequired,
  index: PropTypes.number.isRequired,
  locked: PropTypes.bool,
  comingSoon: PropTypes.bool,
  lockedSubtitle: PropTypes.string,
  reviewCompletedInRetain: PropTypes.bool,
  // lockedSubtitleRedirect: PropTypes.string,
  redirPath: PropTypes.string,
  foundation: PropTypes.bool,
};

TrainingCard.defaultProps = {
  chapterCount: 0,
  chapterCountMin: 0,
  chaptersProgress: {},
  locked: false,
  progress: 0,
  redirPath: '/main/training/chapter/',
  lockedSubtitle: null,
  comingSoon: false,
  reviewCompletedInRetain: false,
  // lockedSubtitleRedirect: '',
  foundation: true,
};

Overlay.propTypes = {
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

TrainingCardLogoImage.propTypes = {
  src: PropTypes.string.isRequired,
};
